import React, { useEffect, useRef, useState } from 'react'
import { Scene } from 'babylonjs/scene'
import { useWindowSize } from 'react-use'
import { Step } from 'react-joyride'
import { makeStyles, Snackbar, Theme } from '@material-ui/core'
// Redux
import { useDispatch, useSelector } from 'react-redux'
import { menuDrawerOpen, toggleDrawer } from 'redux/menu'
// Components
import {
  Contact,
  Header,
  IntroTutorial,
  Loader,
  Modal,
  Navigation,
  Notice,
  ProfileMenu,
  QRCode,
  SceneWrapper,
  Support,
  Tutorial
} from 'components'
import { Video } from 'components/shared'
import { graphQLSubscription } from 'graphql/helpers'
// TODO: rename once we  have the real thing
import { onCreateNotification } from 'graphql/subscriptions'
import Receiver from '../Receiver'
// Helpers
import {
  createInteraction,
  findSessionById,
  introTutorialSteps,
  ISession,
  OldSessions,
  tutorialSteps
} from '../helpers'
import { GameFlowStepsConfig } from '../helpers/steps'
import { ETouchpoints, EventStages, GameFlowSteps, IDemo, ISubscriptionObject, ITeleportLocation, IUser } from 'types'
import { Demos } from 'helpers/demos'
import { Alert } from '@material-ui/lab'
import { VideoChatProvider } from 'providers'
import { JoyrideTutorialStyles } from 'components/shared/tutorial/JoyrideTutorialStyles'
import { useBrowserCache } from '../hooks'
import { AboutEventModal } from 'components/auth/AboutEventModal'
import { VolumeSlider } from '../components/shared/controls/VolumeSlider'

interface IModalConfig {
  videoSrc?: string
  trackSrc?: string
  streamSrc?: string
  imgSource?: string
  demo?: string
  type?: string
}

interface GameWrapperProps {
  user?: IUser | null
  users?: IUser[]
  eventStage?: EventStages
  streamStartTime?: string
  postLiveStream?: boolean
  setPostLiveStream: (val: boolean) => void
}

export const GameWrapper: React.FC<GameWrapperProps> = ({ user, users, eventStage, streamStartTime }) => {
  const dispatch = useDispatch()

  // Selectors
  const drawerOpen = useSelector(menuDrawerOpen)
  const { width } = useWindowSize()
  const classes = useStyles()

  const localStorage = window.localStorage
  // eslint-disable-next-line
  const [eventStartingSoonState, setEventStartingSoonState] = useState<boolean>(false)
  const [showTooltip, setShowTooltip] = useState<boolean>(false)
  const [breakoutNoticeOpen, setBreakoutNoticeOpen] = useState<boolean>(false)
  const [eventReplayNoticeOpen, setEventReplayNoticeOpen] = useState<boolean>(false)
  const [reservedBreakoutSession, setReservedBreakoutSession] = useState<ISession | undefined>()

  setTimeout(() => {
    setShowTooltip(true)
  }, 8000)

  // notice configs
  // eslint-disable-next-line
  let noticeSubscription = useRef<ISubscriptionObject | null>(null)
  const [welcomeTutorialViewedLoading, welcomeTutorialViewed, setWelcomeTutorialViewed] = useBrowserCache(
    'welcome-tutorial-dismissed'
  )
  const [stepsEnabled, setStepsEnabled] = useState<boolean>(false)
  // eslint-disable-next-line
  const [scene, setScene] = useState<Scene>()
  const [showModal, setShowModal] = useState<boolean>(false)
  const [modalConfig, setModalConfig] = useState<IModalConfig>({})
  const [mapLocation] = useState<ITeleportLocation>({
    babylonParam: 'Hall',
    name: 'Hall'
  })
  const cachedVolume = localStorage.getItem('volume')
  const [volume, setVolume] = useState<number>(cachedVolume ? Number(cachedVolume) : 0.25)
  const [muteAudio, setMuteAudio] = useState<boolean>(true)
  const [steps, setSteps] = useState<Step[]>(tutorialSteps)
  const [gameLoading, setGameLoading] = useState<boolean>(true)
  const [showGUI, setShowGUI] = useState<boolean>(false)
  const [loaderOptions, setLoaderOptions] = useState<any>({})
  const [gameState, setGameState] = useState<GameFlowSteps>(GameFlowSteps.Intro)
  const [prevGameState, setPrevGameState] = useState<GameFlowSteps>(GameFlowSteps.Intro)
  const [activeDemo, setActiveDemo] = useState<IDemo>(Demos[0])
  const [isSession, setIsSession] = useState<boolean>(false)
  // pointing to a random session so I don't have to go undo all the unused session logic throughout the app
  const [activeSession, setActiveSession] = useState<ISession>(OldSessions[0])
  const [activeTouchpoint, setActiveTouchpoint] = useState<ETouchpoints>(ETouchpoints.None)
  const [conversationId, setConversationId] = useState<string>('')
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [infoMessage, setInfoMessage] = useState<string | null>(null)
  const [
    // eslint-disable-next-line
    userLoadedInWhileStreamIsOn,
    setUserLoadedInWhileStreamIsOn
  ] = useState<boolean>(false)
  const [showLivestreamNotice, setShowLivestreamNotice] = useState<boolean>(false)
  const [showSurvey, setShowSurvey] = useState<boolean>(false)

  // set the tutorial as viewed for next visit
  const handleToggleDrawer = () => dispatch(toggleDrawer(!drawerOpen))

  const onCloseModal = () => {
    setShowModal(false)
    setModalConfig({})
  }

  const goto3Dlocation = location => {
    const ifx: HTMLIFrameElement = document.getElementById('ifx') as HTMLIFrameElement
    const welcomeSteps = [GameFlowSteps.Intro, GameFlowSteps.Welcome, GameFlowSteps.Connect]
    let newLocation = location

    if ([GameFlowSteps.Sessions, GameFlowSteps.Explore, GameFlowSteps.Session].includes(location)) {
      window.postMessage(`{"command":"location", "param": "${location}"}`, '*')
    } else if (welcomeSteps.includes(prevGameState) && welcomeSteps.includes(location)) {
      setPrevGameState(location)
      return
    }

    triggerAnimation(prevGameState, newLocation)
    setPrevGameState(location)

    function triggerAnimation(prevLocation, newLocation) {
      if (ifx != null && ifx.contentWindow != null) {
        try {
          //@ts-ignore
          if (ifx.contentWindow.PROJECT) {
            //@ts-ignore
            ifx.contentWindow.PROJECT.AnimatorManager.Instance.GetAnimator('ThriveAnimator').animator.setInteger(
              'State',
              GameFlowStepsConfig[newLocation].animation.state
            )

            // Only reset the scroll when going back to demos / sessions from an unrelated route
            if (
              [GameFlowSteps.BackToExplore, GameFlowSteps.Explore].includes(prevLocation) &&
              newLocation !== GameFlowSteps.Demo
            ) {
              window.setTimeout(() => {
                window.state.resetScroll('demos')
              }, 1000)
            } else if (
              [GameFlowSteps.BackToSessions, GameFlowSteps.Sessions].includes(prevLocation) &&
              newLocation !== GameFlowSteps.Session
            ) {
              window.setTimeout(() => {
                window.state.resetScroll('sessions')
              }, 1000)
            }
          }
        } catch (e) {
          console.error(e)
        }
      }
    }
  }

  const onSceneSetup = () => {
    setScene((window as any)['scene'])
  }

  const toggleTutorial = (bool: boolean) => {
    // setShowTutorial(bool)
    setStepsEnabled(false)
    localStorage.setItem('tutorialEnabled', bool.toString())
  }

  ///////////////////////////////////////////////////////////
  // Babylon Scene Loader Window Hooks
  ///////////////////////////////////////////////////////////

  window['loadScene'] = (sceneFile: string, queryString?: string) => {
    if (window.state != null && window.state.showSceneLoader != null) {
      window.state.showSceneLoader(true)
    } else {
      setGameLoading(true)
      setLoaderOptions({ indeterminate: false, percentLoaded: 0 })
    }
    let url: string = `${process.env.PUBLIC_URL}/babylon/engine.html?scene=${sceneFile}`
    if (queryString != null) url += queryString
    const ifx: HTMLIFrameElement = document.getElementById('ifx') as HTMLIFrameElement
    if (ifx != null) {
      if (ifx.contentWindow != null) {
        ifx.contentWindow.location.replace(url)
      } else ifx.src = url
    }
  }

  window['updateStatus'] = (status: string, details: string, state: number) => {
    if (window.state != null && window.state.updateSceneLoader != null) {
      window.state.updateSceneLoader(status, details, state)
    } else {
      setLoaderOptions({
        indeterminate: true,
        loadingStatus: status,
        loadingDetails: details,
        loadingState: state
      })
    }
  }

  window['updateProgress'] = (progress: number) => {
    if (window.state != null && window.state.tickSceneLoader != null) {
      window.state.tickSceneLoader(progress)
    } else {
      setLoaderOptions({ indeterminate: false, percentLoaded: progress })
    }
  }

  window['showGameLoading'] = (show: boolean) => {
    setGameLoading(show)
  }

  window['loadSceneComplete'] = () => {
    if (!window.scene) {
      window.scene = true
      window.state?.resetScroll?.('demos')
      window.state?.resetScroll?.('sessions')
      window.postMessage('{"command":"initialised"}', '*') // Tell React part that the scene is set up
    }
    if (window.state != null && window.state.showSceneLoader != null) {
      window.state.showSceneLoader(false)
    } else {
      setGameLoading(false)
      setTimeout(() => {
        setShowGUI(true)
      }, GameFlowStepsConfig[GameFlowSteps.Intro].animation.time)
    }

    // Set music volume on room change
    setMusicVolume()
  }

  ///////////////////////////////////////////////////////////
  // Babylon Web Socket Window Hook
  ///////////////////////////////////////////////////////////
  window['socketConnect'] = connection => {
    if (window['io'] != null) {
      if (window.state == null) window.state = {}
      window.state['socket'] = window['io'].connect(connection, {
        transports: ['websocket']
      })
      if (window.state['socket'] != null) {
        window.state['socket'].on('connect', () => {
          if (window.state.onSocketConnect) {
            window.state.onSocketConnect()
          }
          window.state['socket'].on('disconnect', () => {
            if (window.state.onSocketDisconnect) {
              window.state.onSocketDisconnect()
            }
          })
        })
      }
    }
    return window.state != null && window.state['socket'] != null ? window.state['socket'] : null
  }

  const newNotificationCreated = ({ onCreateNotification }) => {
    if (onCreateNotification.type === 'survey') {
      setShowModal(true)
      return setShowSurvey(true)
    }
  }
  ///////////////////////////////////////////////////////////
  useEffect(() => {
    setLoaderOptions({ indeterminate: false, percentLoaded: 0 })

    const defaultSceneFile = 'VerizonPublicSector.gltf'

    Receiver.init(
      onSceneSetup,
      setGameState,
      setActiveDemo,
      setActiveSession,
      setActiveTouchpoint,
      setModalConfig,
      setShowModal,
      setConversationId,
      setIsSession,
      user,
      eventStage
    )

    // Track when a user enters the event
    createInteraction('welcome', user?.email || '', 'welcome', 'joined', eventStage)

    window.loadScene(defaultSceneFile)

    if (eventStage === EventStages.LIVESTREAM) {
      setShowLivestreamNotice(true)
      setUserLoadedInWhileStreamIsOn(true)
    } else if (eventStage === EventStages.POSTEVENT) {
      setEventReplayNoticeOpen(true)
    } else if (eventStage === EventStages.POSTLIVESTREAM || eventStage === EventStages.LIVESTREAMENDING) {
      setBreakoutNoticeOpen(true)
    }

    noticeSubscription.current = graphQLSubscription(onCreateNotification, {}, newNotificationCreated)
    // eslint-disable-next-line
  }, [])

  const setMusicVolume = (mute?: boolean) => {
    try {
      const ifx: HTMLIFrameElement = document.getElementById('ifx') as HTMLIFrameElement
      //@ts-ignore
      if (ifx?.contentWindow?.MVRK?.SceneSoundSystem?.MUSIC?.setGroupVolume) {
        if (mute) {
          //@ts-ignore
          ifx.contentWindow.MVRK.SceneSoundSystem.MUSIC.setGroupVolume(0)
        } else {
          //@ts-ignore
          ifx.contentWindow.MVRK.SceneSoundSystem.MUSIC.setGroupVolume(volume)
          localStorage.setItem('volume', volume.toString())
        }
      }
    } catch (err) {
      console.error(err)
      // Sentry.captureException(err)
    }
  }

  useEffect(() => {
    setMusicVolume()
    // eslint-disable-next-line
  }, [volume])

  useEffect(() => {
    setMusicVolume(muteAudio)
    // eslint-disable-next-line
  }, [muteAudio])

  // const getDetailsForReservedBreakoutSession = async () => {
  //   let reservedSessionId = user?.sessions?.items?.[0]?.sessionId
  //   if (reservedSessionId) {
  //     setReservedBreakoutSession(findSessionById(reservedSessionId))
  //   } else if (setReservedBreakoutSession) {
  //     setReservedBreakoutSession(undefined)
  //   }
  // }
  // eslint-disable-next-line
  const breakoutSessionClicked = (sessionLink: string) => {
    createInteraction(sessionLink, user?.email || '', 'sessions', 'joined', eventStage)
  }

  // useEffect(() => {
  //   if (breakoutNoticeOpen !== undefined) {
  //     getDetailsForReservedBreakoutSession()
  //   }
  //   // eslint-disable-next-line
  // }, [breakoutNoticeOpen])
  // eslint-disable-next-line
  const goToReservedSession = () => {
    if (reservedBreakoutSession) {
      createInteraction(
        reservedBreakoutSession.bluejeans || 'bluejeans',
        user?.email || '',
        'sessions',
        'joined',
        eventStage
      )
      // go to the breakout session set as the user's resereved one
      window.open(reservedBreakoutSession.bluejeans, '_blank', 'noopener,noreferrer')
    } else {
      // or go to the breakout session monoliths so the user can choose
      setGameState(GameFlowSteps.Sessions)
    }
    setBreakoutNoticeOpen(false)
    setEventStartingSoonState(false)
    setReservedBreakoutSession(undefined)
  }

  // Updates the tutorial steps on window size change
  useEffect(() => {
    if (width > 766) {
      setSteps(tutorialSteps)
    } else {
      // Filter out the map step on mobile and update the placement of the menu and chat steps
      const steps = tutorialSteps
        .filter(step => step.target !== '.map-marker')
        .map(step => {
          if (step.target === '#menu-icon' || step.target === '#chat-icon') {
            step.placement = 'bottom-start'
          }
          return step
        })

      setSteps(steps)
    }
  }, [width])

  useEffect(() => {
    goto3Dlocation(gameState)

    if ([GameFlowSteps.Demo, GameFlowSteps.Session, GameFlowSteps.EventReplay].includes(gameState) && !muteAudio) {
      setMuteAudio(true)
    } else if (muteAudio) {
      setMuteAudio(false)
    }
    // eslint-disable-next-line
  }, [gameState])

  useEffect(() => {
    if (
      eventStage === EventStages.LIVESTREAM ||
      eventStage === EventStages.POSTSHOW ||
      eventStage === EventStages.COUNTDOWN
    ) {
      setShowLivestreamNotice(true)
    } else if (eventStage && [EventStages.LIVESTREAMENDING, EventStages.POSTLIVESTREAM].includes(eventStage)) {
      setBreakoutNoticeOpen(true)
    } else if (eventStage && eventStage === EventStages.POSTEVENT) {
      setEventReplayNoticeOpen(true)
    }
    // eslint-disable-next-line
  }, [eventStage])

  useEffect(() => {
    if (!drawerOpen && conversationId) {
      handleToggleDrawer()
    }
    // eslint-disable-next-line
  }, [conversationId])

  // const noticeButtonClick = () => {
  //   switch (activeNotice.type) {
  //     case "demo":
  //       // go to demos
  //       setGameState(GameFlowSteps.Explore);
  //       break;
  //     case "session":
  //       // go to a specific session
  //       setGameState(GameFlowSteps.Session);
  //       setActiveSession(Sessions[activeNotice?.link || "financialServices"]);
  //       break;
  //     case "external":
  //       // open an external link in a new tab
  //       window.open(activeNotice.link);
  //       break;
  //     default:
  //       console.warn("notice");
  //   }
  //   setActiveNotice({});
  // };

  useEffect(() => {
    if (gameLoading) {
      return
    }
    const sessionId = new URLSearchParams(window.location.search).get('sessionId')
    if (!sessionId) {
      return
    }

    if (sessionId === 'home') {
      setGameState(GameFlowSteps.Sessions)
      return
    }

    const session = findSessionById(sessionId)
    if (!session) {
      return
    }
    setGameState(GameFlowSteps.Session)
    // setActiveSession(session)
  }, [gameLoading])

  const setShowSurveyModal = (show: boolean) => {
    setShowModal(show)
    setShowSurvey(show)
  }

  return (
    <div id='game' className={classes.gameContainer}>
      <iframe
        id='ifx'
        className={classes.frameContainer}
        width='100%'
        height='100%'
        scrolling='no'
        frameBorder='0'
        title='ifx'
      />
      {gameLoading && <Loader loaderOptions={loaderOptions} />}
      <VideoChatProvider>
        {!gameLoading && showGUI && (
          <>
            <Header setActiveTab={setGameState}>
              <Navigation
                activeTab={gameState}
                setActiveTab={setGameState}
                streamStartTime={streamStartTime}
                eventStage={eventStage}
                notifyESS={() => setEventStartingSoonState(true)}
                isSession={isSession}
                setIsSession={setIsSession}
              />
            </Header>
            <SceneWrapper
              changeActiveTouchpoint={setActiveTouchpoint}
              changeScene={setGameState}
              activeScene={gameState}
              prevScene={prevGameState}
              user={user}
              activeDemo={activeDemo}
              activeSession={activeSession}
              activeTouchpoint={activeTouchpoint}
              drawerOpen={drawerOpen}
              setSuccessMessage={setSuccessMessage}
              setErrorMessage={setErrorMessage}
              eventStage={eventStage}
              isSession={isSession}
              setIsSession={setIsSession}
            />
            <ProfileMenu
              toggleTutorial={() => toggleTutorial(true)}
              toggleIntroTutorial={() => setWelcomeTutorialViewed(false)}
              toggleDrawer={handleToggleDrawer}
              setGameState={setGameState}
              mapLocation={mapLocation}
              drawerOpen={drawerOpen}
              user={user}
              users={users}
              conversationId={conversationId}
              setConversationId={setConversationId}
              eventStage={eventStage}
            />
            <VolumeSlider volume={volume} setVolume={setVolume} drawerOpen={drawerOpen} />
            <Tutorial run={stepsEnabled} steps={steps} onClose={() => toggleTutorial(false)} />
            {modalConfig.videoSrc && <Video videoSrc={modalConfig.videoSrc || ''} />}
            <Modal
              open={Boolean(showModal && modalConfig && modalConfig.imgSource && welcomeTutorialViewed)}
              onClose={onCloseModal}
              className='responsive-modal modal-video-only'
            >
              <img src={modalConfig.imgSource} style={{ height: '100%', width: '100%' }} alt='Modal' />
            </Modal>
            <QRCode showModal={Boolean(showModal && showSurvey)} setShowModal={setShowSurveyModal} />
            {welcomeTutorialViewed && showModal && modalConfig.type === 'about' && (
              <AboutEventModal open={showModal} onClose={() => setShowModal(false)} />
            )}
            {welcomeTutorialViewed && showModal && modalConfig.type === 'connect' && (
              <Contact
                setSuccessMessage={setSuccessMessage}
                setErrorMessage={setErrorMessage}
                showModal={showModal}
                onCloseModal={onCloseModal}
                user={user}
                demo={modalConfig.demo}
              />
            )}
            {welcomeTutorialViewed && showModal && modalConfig.type === 'support' && (
              <Support
                setSuccessMessage={setSuccessMessage}
                setErrorMessage={setErrorMessage}
                showModal={showModal}
                onCloseModal={onCloseModal}
                user={user}
              />
            )}

            {showTooltip && !welcomeTutorialViewedLoading && showGUI && (
              <IntroTutorial
                run={!welcomeTutorialViewed}
                steps={introTutorialSteps(user)}
                onClose={() => {
                  setWelcomeTutorialViewed(true)
                }}
                styles={JoyrideTutorialStyles}
                setGameState={setGameState}
              />
            )}

            {/* Post-Livestream Breakout Notice */}
            {breakoutNoticeOpen && welcomeTutorialViewed ? (
              <Notice
                message={'Watch Verizon partners discuss 5G and its implications on their DOD specific solutions.'}
                onClick={() => {
                  setGameState(GameFlowSteps.Sessions)
                  setBreakoutNoticeOpen(false)
                }}
                onClose={() => setBreakoutNoticeOpen(false)}
                buttonText='DOD 5G use cases'
              />
            ) : null}

            {/* Post-Event Notice */}
            {eventReplayNoticeOpen && welcomeTutorialViewed ? (
              <Notice
                message={
                  'See why 5G matters to the DOD, how it can help securely shape missions, and hear defense-focused use cases.'
                }
                onClick={() => {
                  setGameState(GameFlowSteps.EventReplay)
                  setEventReplayNoticeOpen(false)
                }}
                onClose={() => setEventReplayNoticeOpen(false)}
                buttonText='Watch event replay'
              />
            ) : null}

            {showLivestreamNotice && welcomeTutorialViewed && eventStage === EventStages.POSTSHOW && (
              <Notice
                message='Our keynote speaker and fireside chat are about to begin. Join the live stream now.'
                streamUrl='/stream'
                onClose={() => setShowLivestreamNotice(false)}
              />
            )}

            {showLivestreamNotice &&
            welcomeTutorialViewed &&
            (eventStage === EventStages.COUNTDOWN || eventStage === EventStages.LIVESTREAM) ? (
              <Notice
                message={
                  eventStage === EventStages.COUNTDOWN
                    ? 'The live stream is about to begin.'
                    : 'The live stream is now in session.'
                }
                streamUrl='/stream'
                onClose={() => setShowLivestreamNotice(false)}
              />
            ) : null}

            {(Boolean(successMessage) || Boolean(errorMessage) || Boolean(infoMessage)) && (
              <Snackbar
                className={classes.toastPosition}
                open={Boolean(successMessage) || Boolean(errorMessage) || Boolean(infoMessage)}
                autoHideDuration={5000}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                onClose={() => {
                  successMessage && setSuccessMessage(null)
                  errorMessage && setErrorMessage(null)
                  infoMessage && setInfoMessage(null)
                }}
              >
                <Alert severity={successMessage ? 'success' : infoMessage ? 'info' : 'error'} variant='filled'>
                  {successMessage ? successMessage : infoMessage ? infoMessage : errorMessage}
                </Alert>
              </Snackbar>
            )}
          </>
        )}
      </VideoChatProvider>
    </div>
  )
}

const useStyles = makeStyles((theme: Theme) => ({
  toastESSTitle: {},
  toastESSButton: {
    height: '32px',
    fontSize: '12px',
    margin: '0.5em',
    padding: '6px 12px',
    width: 'auto',
    display: 'flex',
    flexWrap: 'nowrap',
    justifyContent: 'center',
    alignItems: 'center',
    '&:hover': {
      backgroundColor: 'black',
      color: 'white'
    }
  },
  toastPosition: {
    right: 88,
    zIndex: 13000
  },
  gameContainer: {
    backgroundColor: '#e3e3e3'
  },
  frameContainer: {
    marginTop: 60,
    height: 'calc(100vh - 60px)'
  },
  modal: {
    position: 'fixed',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    zIndex: 9999,
    height: '100vh',
    width: '100vw',
    top: 0,
    left: 0
  },
  modalBody: {
    backgroundColor: '#fff',
    width: 560,
    position: 'relative',
    borderBottom: '4px solid #000'
  },
  closeButtonModal: {
    position: 'absolute',
    right: 0,
    top: 0
  },
  postShow: {
    width: 1099,
    height: 715,
    backgroundColor: '#fff',
    position: 'relative',
    [theme.breakpoints.down('md')]: {
      width: '70%',
      maxHeight: '90%'
    }
  },
  postShowHeader: {
    marginBottom: 32,

    [theme.breakpoints.down('sm')]: {
      marginBottom: 20
    }
  },
  notificationIconWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#000',
    borderRadius: '50%',
    height: 45,
    width: 45,
    marginBottom: '1.5rem'
  },
  notificationIcon: {
    color: '#fff',
    width: '25px'
  },
  link: {
    color: '#000',
    textDecoration: 'none',
    cursor: 'pointer'
  }
}))
